import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Form, Input, Row, Typography } from 'antd';
import Lottie from "lottie-react";
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import leads from "../../assets/leads.json";
import logo from "../../assets/tamansari-color.png";
import wrLogo from "../../assets/wr-color.png";
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, isAuthenticated } = useAuth();
  const [form] = Form.useForm();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onFinish = async (values: any) => {
    setLoading(true);
    setError('');
    const result = await login(values.email, values.password);

    if (!result.success && result.error) {
      const errorData = result.error;
      let displayMessage = 'Terjadi kesalahan yang tidak diketahui.';

      // Skenario 5: Akun tidak aktif
      if (errorData.message === 'Account is inactive') {
        displayMessage = 'Akun Anda tidak aktif. Silakan hubungi admin.';
      }
      // Skenario 4 (Pesan Bahasa Inggris): Akun dibekukan
      else if (errorData.message === 'Account is locked due to too many failed login attempts. Please contact administrator.') {
        displayMessage = 'Akun Anda telah dibekukan karena terlalu banyak percobaan login. Silakan hubungi admin.';
      }
      // Skenario 1, 2, 3, 4: Kredensial tidak valid
      else if (errorData.message === 'Invalid credentials') {
        const { email, remaining_attempts } = errorData.data || {};

        // Skenario 1: Email tidak ada di database. Ini prioritas.
        if (email === false) {
          displayMessage = 'Email atau password yang Anda masukkan salah.';
        }
        // Email ada, maka kita periksa sisa percobaan.
        else if (email === true && typeof remaining_attempts !== 'undefined') {
          // Skenario 4: Akun dibekukan
          if (remaining_attempts === 0) {
            displayMessage = 'Akun Anda telah dibekukan karena terlalu banyak percobaan login. Silakan hubungi admin.';
          }
          // Skenario 2 & 3: Masih ada sisa percobaan
          else {
            displayMessage = `Email atau password salah. Sisa percobaan: ${remaining_attempts}.`;
          }
        } else {
          // Fallback jika data tidak lengkap
          displayMessage = 'Email atau password yang Anda masukkan salah.';
        }
      }
      // Menangani error lain dari backend atau error jaringan
      else if (errorData.message) {
        displayMessage = errorData.message;
      }

      setError(displayMessage);
    }
    
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'white',
    }}>
      <Row gutter={[32, 32]} align="middle" style={{ width: '100%' }}>
        <Col xs={24} md={12}>
          <div style={{
            textAlign: 'center',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <Lottie
                animationData={leads}
                loop={true}
                style={{
                  width: '400px',
                  height: 'auto'
                }}
              />
            </div>

            <Title level={1} style={{ color: '#595757', margin: 0 }}>
              Leads Management System
            </Title>
            <Text style={{
              fontSize: 18,
              color: '#595757',
              display: 'block',
              marginTop: 16,
              maxWidth: '500px',
              lineHeight: 1.6
            }}>
              Solusi terpadu untuk mengelola leads dan properti
            </Text>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div style={{ textAlign: 'center' }}>
            <img src={logo} alt="Leads Logo" style={{ width: 300 }} />
          </div>
          <Card
            style={{
              maxWidth: 400,
              margin: '0 auto',
              background: 'rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: 12,
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 50, marginTop: 20 }}>
              <Title level={2} style={{ color: '#595757', marginBottom: 4 }}>
                Login
              </Title>
              <Text type="secondary">
                Masuk ke akun Anda untuk melanjutkan
              </Text>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                style={{ marginBottom: 24 }}
                showIcon
              />
            )}

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Email wajib diisi!' },
                  { type: 'email', message: 'Format email tidak valid!' },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Password wajib diisi!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{
                    width: '100%',
                    height: 48,
                    backgroundColor: '#7BAA18',
                    borderColor: '#7BAA18'
                  }}
                >
                  Masuk
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <div style={{
            fontSize: 12,
            color: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px'
          }}>
            Supported by
            <img src={wrLogo} alt="WIKA Realty Logo" style={{ marginLeft: '8px', height: '30px' }} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
